/*
===============================================================================================================
                                 Copyright 2024-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import SimpleButton from '@/components/button-simple/button-simple';
import InlineEditableText from '@/components/inline-editable-text';
import React from 'react';
import PropTypes from 'prop-types';
import EditableTooltip from '@/components/editable-tooltip/editable-tooltip';
/**
 * A simple button component with inline editable text
 * @param {object} props the parameters for the button
 * @param {string} props.inputSelectorId an ID to be passed to the button component
 * @param {function} props.onClick the button onclick handler
 * @param {string} props.inputHeight a value to configure the min-height
 * @param {string} props.inputWidth a value to configure the min-width
 * @param {boolean} props.fullWidth whether the button should be full width
 * @param {string} props.fontSize a value to configure the font-size
 * @param {boolean} props.hasMargin whether the button should have a margin-top
 * @param {boolean} props.isLozenge whether the button should have curved borders
 * @param {boolean} props.disabled whether the button should be disabled
 * @param {boolean} props.readOnly whether the button should be read only
 * @param {boolean} props.centered whether the button should be centered
 * @param {boolean} props.useMerchantThemeColor whether the button should use the merchant theme color
 * @param {string} props.backgroundColor the background color of the button
 * @param {string} props.textColor the text color of the button
 * @param {object} props.titleProps the title props for the button
 * @param {string} props.titleProps.defaultResetValue the default reset value for the title
 * @param {object} props.tooltipProps the tooltip props for the button
 * @return {JSX.Element}
 */

function InlineEditableButton(props) {
  const {
    inputSelectorId,
    inputHeight,
    inputWidth,
    fullWidth,
    fontSize,
    hasMargin,
    isLozenge,
    disabled,
    readOnly,
    centered,
    useMerchantThemeColor,
    backgroundColor,
    textColor,
    titleProps,
    tooltipProps,
  } = props;
  return (
    <>
      <SimpleButton
        inputSelectorId={inputSelectorId}
        inputHeight={inputHeight}
        inputWidth={inputWidth}
        fullWidth={fullWidth}
        fontSize={fontSize}
        hasMargin={hasMargin}
        isLozenge={isLozenge}
        disabled={disabled}
        readOnly={readOnly}
        centered={centered}
        useMerchantThemeColor={useMerchantThemeColor}
        backgroundColor={backgroundColor}
        textColor={textColor}
        type="button"
        as="div"
      >
        <div className="center">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <InlineEditableText inputSelectorId={`${inputSelectorId}-title`} {...titleProps} />
          {tooltipProps && (
          <div>
            <EditableTooltip
              inputSelectorId={`${inputSelectorId}-tooltip`}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tooltipProps}
              onChangeValidationRules={tooltipProps.validationRules?.onChange}
              onSubmitValidationRules={tooltipProps.validationRules?.onSubmit}
            />
          </div>
          )}
        </div>
      </SimpleButton>
      <style jsx>
        {`
          .center {
            display: flex;
            justify-content: center;
            align-items: center;
          }
        `}
      </style>
    </>
  );
}

InlineEditableButton.defaultProps = {
  inputSelectorId: '',
  onClick: () => {},
  inputHeight: 'auto',
  inputWidth: 'auto',
  fullWidth: false,
  fontSize: 'inherit',
  hasMargin: false,
  isLozenge: false,
  disabled: false,
  readOnly: false,
  centered: false,
  useMerchantThemeColor: false,
  backgroundColor: 'var(--color-primary)',
  textColor: 'var(--color-white)',
};

InlineEditableButton.propTypes = {
  inputSelectorId: PropTypes.string,
  onClick: PropTypes.func,
  inputHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inputWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fullWidth: PropTypes.bool,
  fontSize: PropTypes.string,
  hasMargin: PropTypes.bool,
  isLozenge: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  centered: PropTypes.bool,
  useMerchantThemeColor: PropTypes.bool,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  titleProps: PropTypes.shape({
    defaultResetValue: PropTypes.string,
  }).isRequired,
  tooltipProps: PropTypes.shape({
    defaultResetValue: PropTypes.string,
    description: PropTypes.string,
    validationRules: PropTypes.shape({
      onChange: PropTypes.arrayOf(PropTypes.string),
      onSubmit: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
};

export default InlineEditableButton;
